import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselectedPlan = params.get("plan");

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("¡Cuenta creada!");
      // If a paid plan was requested, redirect to Stripe checkout
      if (preselectedPlan && preselectedPlan !== "free") {
        try {
          const { data } = await api.post("/payments/checkout", {
            lookup_key: preselectedPlan,
            origin_url: window.location.origin,
          });
          window.location.href = data.checkout_url;
          return;
        } catch (e2) {
          toast.error("No se pudo iniciar el pago: " + apiErr(e2));
        }
      }
      navigate("/app");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] p-12 text-white">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-blue-600 grid place-items-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display font-bold text-lg notranslate" translate="no">CHRONOVIA</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">
              Facturación
            </div>
          </div>
        </Link>
        <div>
          <h2 className="font-display text-3xl font-bold leading-tight">
            Empieza a facturar como un profesional.
          </h2>
          <ul className="mt-8 space-y-3 text-slate-300 text-sm">
            <li>• Presupuestos y facturas con PDF y XML</li>
            <li>• Envío automático a tu gestoría</li>
            <li>• Calendario fiscal precargado (Modelos 303, 130, 390...)</li>
            <li>• 5 facturas al mes gratis, sin tarjeta</li>
          </ul>
        </div>
        <div className="text-xs text-slate-500">
          © {new Date().getFullYear()} <span className="notranslate" translate="no">CHRONOVIA</span> · Hecho en España
          <br />
          <a href="mailto:info@autonomopro.online" className="text-slate-400 hover:text-slate-200 transition">
            info@autonomopro.online
          </a>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Crea tu cuenta
          </h1>
          <p className="text-slate-500 mt-2">
            {preselectedPlan && preselectedPlan !== "free"
              ? `Regístrate para activar el plan ${
                  preselectedPlan === "pro_monthly" ? "Pro" : "Business"
                }`
              : "Es gratis, no necesitas tarjeta."}
          </p>

          <form className="mt-8 space-y-4" onSubmit={submit}>
            <div>
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                required
                value={form.name}
                onChange={setField("name")}
                placeholder="María López"
                data-testid="register-name-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={setField("email")}
                placeholder="tucorreo@ejemplo.com"
                data-testid="register-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={setField("password")}
                placeholder="Mínimo 6 caracteres"
                data-testid="register-password-input"
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 h-11"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? "Creando cuenta..." : "Crear cuenta gratis"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            ¿Ya tienes cuenta?{" "}
            <Link
              to="/login"
              className="text-blue-600 hover:underline font-medium"
              data-testid="register-login-link"
            >
              Entrar
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
